import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { TrendingUp, Clock, Target, CheckCircle, ArrowRight } from "lucide-react";
import { SITE } from "../data/seoPages";

const studies = [
  {
    id: "naunki-myntra-rising-star",
    brand: "Naunki",
    industry: "Fashion / Apparel",
    video: "FLnV33mJVx4",
    problem: "A new apparel label with strong designs but no marketplace presence and no clear path through Myntra's selective brand evaluation.",
    solution: "BrandBorn ran a full documentation audit, strengthened brand assets, generated the complete Myntra catalog with EcomCatalog.ai and routed the application through our Official OMS Partner channel.",
    timeline: "Live on Myntra within the 14-day standard",
    results: ["Approved first attempt through OMS routing", "Full catalog cleared Myntra QC on first pass", "Recognized in Myntra's Rising Star program"],
    kpis: [["Approval", "1st attempt"], ["QC pass", "100%"], ["Recognition", "Rising Star"]],
  },
  {
    id: "yanx-myntra-launch",
    brand: "Yanx",
    industry: "Men's Fashion",
    video: "cBQGgP3hMn8",
    problem: "A men's fashion brand needed Myntra access fast to catch a seasonal window, without the multi-month rejection cycles typical of self-service applications.",
    solution: "Parallel-tracked documentation and catalog: eligibility cleared in days, AI-generated Myntra templates for the full range, OMS-routed application and QC support.",
    timeline: "Application to live listings inside one season window",
    results: ["Live on Myntra Rising Star program", "Founder video testimonial published on our Growth Story page", "Season window captured with full assortment live"],
    kpis: [["Launch", "In-season"], ["Catalog", "Full range live"], ["Program", "Rising Star"]],
  },
  {
    id: "hotshot-blinkit-growth",
    brand: "Hotshot",
    industry: "FMCG / Quick Commerce",
    video: "tDoJstRQ05c",
    problem: "A consumer brand wanted quick commerce distribution but had no vendor relationships, no margin benchmarks and no dark-store catalog experience.",
    solution: "BrandBorn managed Blinkit vendor onboarding end-to-end: compliance audit, commercially realistic margin proposal, dark-store-ready catalog and fill-rate planning for early purchase orders.",
    timeline: "First Blinkit purchase order within 4 weeks",
    results: ["Blinkit vendor approval and first POs secured", "Consistent fill rates unlocked dark-store expansion", "Monthly revenue dashboards published openly on our Growth Story page"],
    kpis: [["First PO", "4 weeks"], ["Channel", "Blinkit"], ["Proof", "Public dashboards"]],
  },
  {
    id: "sepik-quick-commerce-trio",
    brand: "Sepik Lifescience",
    industry: "Wellness / FMCG",
    video: "9ktzuyCdnc4",
    problem: "A wellness brand needed multi-platform quick commerce presence — but sequential platform applications would have taken most of a year.",
    solution: "One compliance-complete documentation set powered parallel applications: Swiggy Instamart and Blinkit onboarded together, with EcomCatalog.ai generating platform-specific catalogs for both.",
    timeline: "Both platforms live from a single engagement",
    results: ["Swiggy Instamart + Blinkit live in parallel", "Single documentation set, two platform approvals", "Video testimonial live on our Growth Story page"],
    kpis: [["Platforms", "2 in parallel"], ["Docs", "1 set"], ["Model", "PO-based"]],
  },
];

export default function CaseStudiesPage() {
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE },
          { "@type": "ListItem", position: 2, name: "Case Studies", item: `${SITE}/case-studies` },
        ],
      },
      ...studies.map((s) => ({
        "@type": "VideoObject",
        name: `${s.brand} — ${s.industry} onboarding case study | BrandBorn`,
        description: s.problem,
        thumbnailUrl: `https://i.ytimg.com/vi/${s.video}/hqdefault.jpg`,
        embedUrl: `https://www.youtube.com/embed/${s.video}`,
        uploadDate: "2026-01-15",
        publisher: { "@type": "Organization", name: "BrandBorn", url: SITE },
      })),
      ...studies.map((s) => ({
        "@type": "Article",
        headline: `${s.brand} case study — ${s.industry}`,
        description: `${s.problem} ${s.timeline}.`,
        url: `${SITE}/case-studies#${s.id}`,
        author: { "@type": "Organization", name: "BrandBorn" },
        publisher: { "@type": "Organization", name: "BrandBorn", url: SITE },
      })),
    ],
  };

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="case-studies-page">
      <Helmet>
        <title>Client Case Studies | Real Brand Launch Results — BrandBorn</title>
        <meta name="description" content="Detailed BrandBorn case studies: Myntra Rising Star launches (Naunki, Yanx), Blinkit growth (Hotshot) and quick commerce trios — with video testimonials and KPIs." />
        <link rel="canonical" href={`${SITE}/case-studies`} />
        <meta property="og:title" content="BrandBorn Client Case Studies — Real Launch Results" />
        <meta property="og:description" content="Myntra Rising Star launches, Blinkit revenue growth and quick commerce case studies with video proof." />
        <meta property="og:url" content={`${SITE}/case-studies`} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <section className="px-4 py-14 md:py-20 bg-gradient-to-b from-slate-50 to-white text-center">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-100 text-red-600 rounded-full text-xs font-bold mb-5">
            <TrendingUp size={14} /> CLIENT CASE STUDIES — REAL RESULTS, VIDEO PROOF
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-4">Brand Launch Case Studies</h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Industry, problem, solution, timeline and KPIs — with founder video testimonials. More dashboards and videos on our <Link to="/growth-story" className="text-red-600 font-bold hover:underline">Growth Story page</Link>.
          </p>
        </div>
      </section>

      <section className="px-4 py-8">
        <div className="max-w-5xl mx-auto space-y-10">
          {studies.map((s) => (
            <article key={s.id} id={s.id} className="bg-white rounded-3xl border border-gray-100 shadow-lg overflow-hidden" data-testid={`case-study-${s.id}`}>
              <div className="grid md:grid-cols-2">
                <div className="bg-black">
                  <iframe
                    src={`https://www.youtube.com/embed/${s.video}?rel=0&modestbranding=1`}
                    title={`${s.brand} case study video testimonial`}
                    className="w-full h-full min-h-[280px]"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-3 py-1 bg-red-50 text-red-600 rounded-full text-[11px] font-black uppercase">{s.industry}</span>
                  </div>
                  <h2 className="text-2xl font-black text-gray-900 mb-4">{s.brand}</h2>
                  <div className="space-y-3 text-sm">
                    <p><strong className="text-gray-900 flex items-center gap-1.5"><Target size={14} className="text-red-500" /> Problem</strong><span className="text-gray-600">{s.problem}</span></p>
                    <p><strong className="text-gray-900 flex items-center gap-1.5"><CheckCircle size={14} className="text-green-500" /> Solution</strong><span className="text-gray-600">{s.solution}</span></p>
                    <p><strong className="text-gray-900 flex items-center gap-1.5"><Clock size={14} className="text-blue-500" /> Timeline</strong><span className="text-gray-600">{s.timeline}</span></p>
                  </div>
                  <ul className="mt-4 space-y-1.5">
                    {s.results.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700 font-medium"><CheckCircle size={15} className="text-green-500 mt-0.5 flex-shrink-0" />{r}</li>
                    ))}
                  </ul>
                  <div className="grid grid-cols-3 gap-3 mt-5">
                    {s.kpis.map(([k, v], i) => (
                      <div key={i} className="bg-slate-50 rounded-xl p-3 text-center border border-gray-100">
                        <div className="text-sm font-black text-red-600">{v}</div>
                        <div className="text-[10px] text-gray-500 font-bold uppercase">{k}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="px-4 py-10">
        <div className="max-w-5xl mx-auto flex flex-wrap gap-3 text-sm font-bold">
          <Link to="/growth-story" className="text-blue-600 hover:underline">All Videos & Dashboards →</Link>
          <Link to="/brand-onboarding-agency-india" className="text-blue-600 hover:underline">Onboarding Services →</Link>
          <Link to="/software" className="text-blue-600 hover:underline">AI Software →</Link>
          <Link to="/knowledge" className="text-blue-600 hover:underline">Knowledge Base →</Link>
        </div>
      </section>

      <section className="px-4 py-14 bg-gradient-to-r from-red-600 to-orange-500 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black text-white mb-4">Your Brand Could Be the Next Case Study</h2>
          <a href="tel:+919464645491" className="inline-block px-8 py-4 bg-white text-red-600 rounded-2xl font-black text-sm uppercase tracking-wider hover:-translate-y-0.5 transition-transform shadow-xl">Free Eligibility Check — +91-9464645491</a>
        </div>
      </section>
    </div>
  );
}
